/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */


import React from 'react';
import Select, { OnChangeValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import './ValueList.scss';
import { getOption, initCustomOptions, ReactSelectOptions } from './selectUtils';
import { InputProps } from './InputProps';
import { ValueData } from '../../question/questionInterfaces';

const handleMultipleChoiceChange = (
  newValue: OnChangeValue<ReactSelectOptions, boolean>,
  handleValueDataChange: (data?: any) => string
) => {

  if (Array.isArray(newValue)) {
    return handleValueDataChange(newValue.map(v => v.value));
  }

  if (typeof newValue === 'object' && newValue !== null) {
    const { value } = newValue as ReactSelectOptions;
    handleValueDataChange(value);
    return;
  }

  handleValueDataChange();
};

const reactSelectStyles = {
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: '#2971e5',
    color: 'white',
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: 'white'
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#cacaca'
  })
};

const getSafeValueData = (valueDataData: ValueData['data']): string[] => {
  if (Array.isArray(valueDataData)) {
    return valueDataData.map(v => 'string' === typeof v ? v : '')
  }
  const newValueData: string[] = [];
  if ('string' === typeof valueDataData) {
    newValueData.push(valueDataData);
  }
  return newValueData;
}

interface ValueListProps extends InputProps {
  multi?: boolean;
}

export default function ValueList(props: ValueListProps) {
  const {
    isDisabled,
    saving,
    universalTracker,
    valueDataData,
    handleValueDataChange,
    placeholder
  } = props;

  const allowCustomOptions = !!universalTracker.getValueValidation()?.valueList?.allowCustomOptions;
  const isMultipleChoice = props.multi === true;

  const disabled = isDisabled?.(props);
  const data = getSafeValueData(valueDataData);

  const options = React.useMemo(() => {

    // Convert to ReactSelect option structure
    const utrOption = universalTracker.getValueListOptions().map(vl => ({
      value: vl.code,
      label: vl.name
    }));

    return initCustomOptions(
      data,
      utrOption,
      allowCustomOptions
    );
  }, [data, universalTracker, allowCustomOptions]);

  const selectedOptions: ReactSelectOptions[] = [];
  data.forEach(vd => {
    const option = getOption(options, vd, allowCustomOptions);
    if (!option.isDisabled) {
      selectedOptions.push(option);
    }
  });

  const onChangeHandler = (newValue: OnChangeValue<ReactSelectOptions, boolean>) => {
    handleMultipleChoiceChange(newValue, handleValueDataChange);
  }

  const value = isMultipleChoice ? selectedOptions : selectedOptions[0];

  if (allowCustomOptions) {
    return <CreatableSelect
      placeholder={placeholder}
      className='valueList-react-select'
      isMulti={isMultipleChoice}
      onChange={onChangeHandler}
      value={value ?? null}
      styles={reactSelectStyles}
      isDisabled={disabled || saving}
      isSearchable={true}
      options={options}
    />
  }

  return <Select
    placeholder={placeholder}
    className='valueList-react-select'
    isMulti={isMultipleChoice}
    onChange={onChangeHandler}
    value={value ?? null}
    styles={reactSelectStyles}
    isDisabled={disabled || saving}
    isSearchable={true}
    options={options}
  />
}
